"use client";

import type { ImpactPartner, Media } from "@mw/shared/payload-types";

import { Image } from "@ui/components/core/Image";
import { AspectRatio } from "@ui/components/core/AspectRatio";
import { ImageSizes } from "@ui/lib/image";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { SwitcherButton } from "@ui/components/core/SwitcherButton";
import { useState, useMemo } from "react";
import ImpactPartnerPopup from "./ImpactPartnerPopup";
import { ChevronRight } from "lucide-react";
import { CategoryName, useGetCategoryLabel } from "./ImpactPartnerGridHooks";
import { impactPartnersCategories } from "@mw/shared/impact-partners-config";

const Pill = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return (
    <span
      className={cn(
        "leading-none f-paragraph-03 whitespace-nowrap rounded-lg bg-overlay-dark px-12 py-8 text-default",
        className,
      )}
    >
      {children}
    </span>
  );
};

const Sphere = ({ className }: { className?: string }) => {
  return (
    <div
      className={cn("flex size-12 items-center justify-center rounded-full border border-overlay-darkest", className)}
    ></div>
  );
};

const ImpactPartnerCard = ({ partner, onClick }: { partner: ImpactPartner; onClick: () => void }) => {
  const { description, logo, diagnosisType, type: partnerType } = partner;
  const t = useTranslations("ClientKeys.Components.impactGrid");
  return (
    <button
      className='effect-color group relative flex w-full flex-col justify-end space-y-52 overflow-hidden rounded-xl bg-dark p-20 text-left transition-shadow duration-300 ease-out hover:bg-default hover:shadow-[0_0_0_1px_rgba(0,0,0,0.07),_0_2px_4px_0_rgba(0,0,0,0.04)] lg:space-y-72 lg:p-28'
      type='button'
      onClick={onClick}
    >
      <div className='flex h-32 items-center justify-between'>
        {partnerType === "application" && (
          <>
            <div className='flex h-[1ch] w-full items-center gap-8'>
              <Sphere />
              <span className='f-ui-03 line-clamp-1 max-w-[150px] truncate text-light'>{diagnosisType}</span>
            </div>
            <Pill>{t("application_partner")}</Pill>
          </>
        )}
      </div>

      <div className='flex flex-1 items-center justify-center'>
        <div className='relative size-120'>
          <AspectRatio ratio='1x1' rounded={false}>
            <Image
              resource={logo as Media}
              className='h-auto w-full object-contain'
              sizes={ImageSizes.FULL_1_3_ON_LG}
            />
          </AspectRatio>
        </div>
      </div>
      <div className='flex h-[2lh] flex-col items-end justify-end'>
        <p className='f-paragraph-02 mt-auto line-clamp-2 text-default'>{description}</p>
      </div>
    </button>
  );
};

const ViewAllCard = ({ count, onClick }: { count: number; category: string; onClick?: () => void }) => {
  const t = useTranslations("ClientKeys.Components.impactGrid");

  return (
    <button
      className='effect-color group relative flex min-h-[300px] w-full flex-col items-center justify-center space-y-32 overflow-hidden rounded-xl border bg-default p-20 text-center transition-shadow duration-300 ease-out hover:bg-dark hover:shadow-[0_0_0_1px_rgba(0,0,0,0.07),_0_2px_4px_0_rgba(0,0,0,0.04)] lg:space-y-72 lg:p-28'
      type='button'
      onClick={onClick}
    >
      {/* Spacer */}
      <div className='flex h-[32px] items-center justify-between' />
      <div className='flex flex-1 items-center justify-center'>
        <div className='flex size-120 items-center justify-center'>
          <div className='flex size-72 items-center justify-center rounded-xl border transition-colors'>
            <ChevronRight size={24} className='text-default' />
          </div>
        </div>
      </div>

      {/* Text */}
      <div className='flex h-[2lh] w-full flex-col items-end justify-end'>
        <p className='f-paragraph-02 w-full text-left text-default'>
          {count > 0 ? t("view_more_partners", { count }) : t("view_all_partners")}
        </p>
      </div>
    </button>
  );
};

export default function ImpactPartnerGrid({
  allPartners,
  displayPartners,
  viewAll,
}: {
  allPartners: ImpactPartner[];
  displayPartners: ImpactPartner[];
  viewAll: boolean;
}) {
  const filteredDisplayPartners = displayPartners.length > 0 ? displayPartners : allPartners;
  const filteredAllPartners = allPartners;
  const getCategoryLabel = useGetCategoryLabel();
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [selectedPartner, setSelectedPartner] = useState<ImpactPartner | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // Get unique categories from display partners (for the grid)
  const categories = useMemo(() => ["all", ...impactPartnersCategories], []);
  // Create switcher options
  const switcherOptions = useMemo(() => {
    return categories.map(cat => ({
      id: cat,
      label: getCategoryLabel(cat as CategoryName),
    }));
  }, [categories, getCategoryLabel]);

  // Filter display partners based on selected category
  const displayedPartners = useMemo(() => {
    const selectedCategory = categories[selectedCategoryIndex];
    if (selectedCategory === "all") {
      return filteredDisplayPartners;
    }
    return allPartners.filter(p => p.category === selectedCategory).slice(0, filteredDisplayPartners.length);
  }, [filteredDisplayPartners, categories, selectedCategoryIndex, allPartners]);

  const handlePartnerClick = (partner: ImpactPartner | null) => {
    setSelectedPartner(partner);
    setIsPopupOpen(true);
  };

  return (
    <div className='space-y-20'>
      {/* Category Switcher */}
      {switcherOptions.length > 1 && (
        <div className='flex items-center overflow-x-auto px-16 md:px-48'>
          <SwitcherButton
            options={switcherOptions}
            selectedIndex={selectedCategoryIndex}
            onSelect={setSelectedCategoryIndex}
            className='max-w-lg'
          />
        </div>
      )}

      {/* Partner Grid */}
      <div className='grid grid-cols-1 gap-16 px-8 py-10 md:grid-cols-2 md:px-16 lg:grid-cols-3'>
        {displayedPartners.map(partner => (
          <ImpactPartnerCard key={partner.id} partner={partner} onClick={() => handlePartnerClick(partner)} />
        ))}
        {viewAll && (
          <ViewAllCard
            count={filteredAllPartners.length - filteredDisplayPartners.length}
            category={switcherOptions[selectedCategoryIndex].label}
            onClick={() => handlePartnerClick(null)}
          />
        )}
      </div>

      {/* Partner Popup - uses allPartners for search/modal */}
      <ImpactPartnerPopup
        partners={filteredAllPartners}
        selectedPartner={selectedPartner}
        setSelectedPartner={setSelectedPartner}
        open={isPopupOpen}
        onOpenChange={setIsPopupOpen}
      />
    </div>
  );
}
